import { Box, Grid, Typography, debounce, makeStyles } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AutocompleteInputChangeReason, createFilterOptions } from "@material-ui/lab";
import dayjs from "dayjs";
import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useRecoilState } from "recoil";

import {
  HowMaBuilding,
  HowMaRoom,
  IdName,
  PropertyBuilder,
  PropertyManagementTypeEnum,
  PropertySalesStatusEnum,
  PropertyStructureEnum,
} from "@/api/generated";
import { GetCities, GetPrefectures, GetRailways, GetStations, GetTowns } from "@/api/getAddress";
import { NEW_MANAGEMENT_TYPE_KEY_VALUES } from "@/api/mappings/property";
import { CustomButton } from "@/components/Button";
import { RegistrationActions } from "@/components/property_registration/RegistrationActions";
import { RegistrationSection } from "@/components/property_registration/RegistrationActions";
import { PropertyRegistrationMode } from "@/components/property_registration/RegistrationContext";
import { RegistrationUpload as UploadComponent } from "@/components/property_registration/RegistrationInputs";
import { RegistrationAutocomplete } from "@/components/property_registration/RegistrationInputs";
import {
  RegistrationAutoCompleteForm as CustomAutoCompleteForm,
  CustomForm,
} from "@/components/property_registration/RegistrationInputs";
import { CustomFormLabel } from "@/components/property_registration/RegistrationInputs";
import { CustomSelector } from "@/components/property_registration/RegistrationInputs";
import { DatePickerComponent } from "@/components/property_registration/RegistrationInputs";
import { Toast } from "@/components/Toast";
import { Layout } from "@/constants/addNewProperty";
import {
  convertAreaNameToCity,
  convertAreaNameToPrefecture,
  convertAreaNameToTown,
  convertFloorPlanToLayout,
} from "@/logics/howma/howMaBuildingSearchApiLogic";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { setStoragePropertyRegistrationState } from "@/utilities/storage";
import { PropertyRegistrationState } from "@/view_models/atoms";

const useStyles = makeStyles(() => ({
  content: {
    width: 640,
    margin: "32px auto 32px auto",
    [theme.breakpoints.down("sm")]: {
      width: 342,
      margin: "16px auto 0px auto",
    },
  },
  aiSection: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 8,
    padding: "24px",
    marginTop: "24px",
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.down("sm")]: {
      padding: "16px",
    },
  },
  aiTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1.5),
  },
  aiDescription: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  "@keyframes registrationSpin": { to: { transform: "rotate(360deg)" } },
  spinner: { display: "block", animation: "$registrationSpin 1.4s linear infinite" },
  loadingOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.semantic["surface/overlay"],

    zIndex: 1600,
  },
  loadingCard: {
    width: 294,
    maxWidth: "calc(100vw - 48px)",
    borderRadius: 16,
    padding: "32px 24px",
    backgroundColor: theme.palette.semantic["surface/card"],
    gap: 16,
    [theme.breakpoints.up("md")]: { width: 440, padding: 40 },
  },
  aiLoadingTitle: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.5,
    marginBottom: theme.spacing(0.5),
    textAlign: "center",
  },
  aiLoadingDescription: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.5,
    textAlign: "center",
  },
}));

interface AddNewOwnPropertyBasicPageProps {
  registrationMode?: PropertyRegistrationMode;
  onAiReview?: () => void;
  onNext: () => void;
  validate: () => Promise<boolean>;
}

const AddNewOwnPropertyBasicPage: React.FC<AddNewOwnPropertyBasicPageProps> = ({
  onNext,
  validate,
  registrationMode,
  onAiReview,
}) => {
  const isAiCreatingRef = React.useRef(false);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    setError,
    clearErrors,
  } = useFormContext();

  // デフォルト値
  const [formData, setFormData] = useRecoilState(PropertyRegistrationState);

  // AutoCompleteコンポーネントでWarningを出さずに空文字を許容するために使用
  const defaultFilterOptions = createFilterOptions();

  const [prefectures, setPrefectures] = React.useState<Array<string>>([""]);
  const [cities, setCities] = React.useState<Array<string>>([formData.city ? formData.city : ""]);
  const [towns, setTowns] = React.useState<Array<string>>([""]);
  const [railways, setRailways] = React.useState<Array<IdName>>([]);
  const [stations, setStations] = React.useState<Array<string>>([""]);

  // マンション検索関連_ここから
  const [buildingOptions, setBuildingOptions] = React.useState<Array<HowMaBuilding>>([]);
  const [roomOptions, setRoomOptions] = React.useState<Array<HowMaRoom>>([]);
  const [selectedHowMaBuilding, setSelectedHowMaBuilding] = React.useState<HowMaBuilding>(null);
  const [selectedHowMaRoom, setSelectedHowMaRoom] = React.useState<HowMaRoom>(null);
  const [isBuildingsSearching, setIsBuildingsSearching] = React.useState<boolean>(false);

  // サーバーエラー
  const [serverError, setSeverError] = React.useState<string>("");
  const [isAiCreating, setIsAiCreating] = React.useState(false);

  const watchValues = useWatch({
    name: ["prefecture", "city", "town", "railway", "property_type"],
    control: control,
  });

  /**
   * 関数定義
   */
  const fp = PropallyApiFactory();
  const buildingsSearch = (searchBuildingName: string) => {
    setValue("name", searchBuildingName);
    // 入力し直しの場合は選択肢は空にする
    setBuildingOptions([]);
    setRoomOptions([]);

    if (searchBuildingName.length < 2) return;

    setIsBuildingsSearching(true);
    debounceBuildingsSearch(searchBuildingName);
  };

  const debounceBuildingsSearch = debounce(
    (searchBuildingName: string) => {
      fetchBuildingOptions(searchBuildingName);
    },
    1000 // 1秒
  );

  // 建物情報取得
  const fetchBuildingOptions = (searchBuildingName: string) => {
    fp.v1HowmaBuildingsPost({ text: searchBuildingName }, { withCredentials: true })
      .then((res) => {
        setBuildingOptions(res.data.buildings);
        setIsBuildingsSearching(false);
      })
      .catch(() => {
        setSeverError("物件検索に失敗しました。再度お試し頂くか、手動で入力してください。");
        setIsBuildingsSearching(false);
      });
  };

  // 部屋情報取得
  const fetchRoomOptions = (buildingId: number) => {
    fp.v1HowmaBuildingsBuildingIdGet(buildingId, { withCredentials: true })
      .then((res) => {
        setRoomOptions(res.data.rooms);
      })
      .catch(() => {
        setSeverError("物件検索に失敗しました。再度お試し頂くか、手動で入力してください。");
      });
  };

  /** Load prefectures and railways at once */
  React.useEffect(() => {
    let isMounted = true;
    (async () => {
      {
        const r = await GetPrefectures();
        if (isMounted) {
          if (!r.success) {
            return setSeverError(r.error);
          }
          setPrefectures(r.prefectures);
        }
      }

      {
        const r = await GetRailways();
        if (isMounted) {
          if (!r.success) {
            return setSeverError(r.error);
          }

          setRailways(r.railways);
        }
      }
      return undefined;
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  /** Load cities for each time prefecture is selected */
  React.useEffect(() => {
    if (!watchValues.prefecture) return;
    if (watchValues.prefecture === "") {
      setValue("city", "");
      setValue("town", "");
      setCities([""]);
      setTowns([""]);
      return;
    }

    let isMounted = true;
    (async () => {
      const r = await GetCities(watchValues.prefecture);
      if (isMounted) {
        if (!r.success) {
          return setSeverError(r.error);
        }

        setCities(r.cities);
        if (selectedHowMaBuilding) {
          // マンション名候補から選択時
          setValue("city", convertAreaNameToCity(selectedHowMaBuilding.area.name, r.cities));
        }
      }
      return undefined;
    })();
    return () => {
      isMounted = false;
    };
  }, [watchValues.prefecture]);

  /** Load towns for each time city is selected */
  React.useEffect(() => {
    if (!watchValues.city) return undefined;
    if (watchValues.city === "") return undefined;
    let isMounted = true;

    (async () => {
      const r = await GetTowns(watchValues.prefecture, watchValues.city);
      if (isMounted) {
        if (!r.success) {
          return setSeverError(r.error);
        }
        setTowns(r.towns);
        if (selectedHowMaBuilding) {
          // マンション名候補から選択時
          setValue("town", convertAreaNameToTown(selectedHowMaBuilding.area.name, r.towns));
        }
      }

      return undefined;
    })();

    return () => {
      isMounted = false;
    };
  }, [watchValues.city]);

  /** Load stations for each time railway is selected */
  React.useEffect(() => {
    if (!watchValues.railway) return undefined;
    if (watchValues.railway === "") return undefined;
    let isMounted = true;

    (async () => {
      const r = await GetStations(watchValues.railway);
      if (isMounted) {
        if (!r.success) {
          // return enqueueSnackbar(r.error);
          return setSeverError(r.error);
        }
        setStations(r.stations);
        if (selectedHowMaBuilding) {
          // マンション名候補から選択時
          setValue("station", selectedHowMaBuilding.station?.name);
        }
      }
      return undefined;
    })();
    return () => {
      isMounted = false;
    };
  }, [watchValues.railway]);

  /**
   * マンション名候補から選択した場合の自動入力処理
   * ※市区町村, 番地, 駅名の自動入力はAutocompleteの選択肢がセットされてから実施するため、それぞれの各マスタuseEffect箇所でそれぞれ実施
   */
  React.useEffect(() => {
    if (!selectedHowMaBuilding) return;
    setValue("name", selectedHowMaBuilding.name);
    setValue("prefecture", convertAreaNameToPrefecture(selectedHowMaBuilding.area.name, prefectures));
    setValue("railway", railways.find((v) => v.id === selectedHowMaBuilding.station?.line_cd)?.name ?? "");
    setValue("built_at", selectedHowMaBuilding.built_at_year.toString());
  }, [selectedHowMaBuilding]);

  /** 号室候補から選択した場合の自動入力処理 */
  React.useEffect(() => {
    if (!selectedHowMaRoom) return;
    setValue("room_number", selectedHowMaRoom.name);
    setValue("floor", String(selectedHowMaRoom.floor_number));
    setValue("occupied_area_m2", String(selectedHowMaRoom.m2));
    setValue("layout", convertFloorPlanToLayout(selectedHowMaRoom.floor_plan));
  }, [selectedHowMaRoom]);

  const handleNext = async () => {
    const isValid = await validate();
    if (isValid) {
      const saveFormData = {
        ...getValues(),
        ...formData,
      };
      setStoragePropertyRegistrationState(saveFormData);
      sendEventToAppMeasurementTool(AppEventTokenEnum.OwnPropertyBasicInfoCompleted);
      onNext();
    }
  };

  const toStringOrUndefined = (value?: string | number | null) => {
    if (value === null || value === undefined) return undefined;
    const str = typeof value === "number" ? value.toString() : value;
    return str.trim() === "" ? undefined : str;
  };
  const toYearMonthOrUndefined = (value: unknown) => {
    if (value === null || value === undefined || value === "") return undefined;
    const parsed = dayjs(value as dayjs.ConfigType);
    if (!parsed.isValid()) return undefined;
    return parsed.format("YYYY-MM");
  };

  const toYearMonthAndDay = (value?: string | null) => {
    if (!value) return { yearMonth: undefined, day: undefined };
    const parsed = dayjs(value);
    if (!parsed.isValid()) return { yearMonth: undefined, day: undefined };
    return { yearMonth: parsed.format("YYYY-MM"), day: parsed.format("D") };
  };

  const mapDraftToFormSchema = (draft: PropertyBuilder) => {
    const boughtAt = toYearMonthAndDay(draft.bought_at);
    const paymentStartsAt = toYearMonthAndDay(draft.payment_starts_at);
    const leaseContract =
      draft.lease_contract ?? draft.lease_contract_current ?? draft.lease_contract_latest ?? draft.lease_contracts?.[0];
    const leaseContractStartsAt = toYearMonthAndDay(leaseContract?.starts_at);
    const leaseContractEndsAt = toYearMonthAndDay(leaseContract?.ends_at);
    const cashflow =
      draft.cashflow_result_of_the_first_month ??
      draft.monthly_cashflow_result_latest ??
      draft.monthly_cashflow_results?.[0];

    const builtAtYear =
      draft.built_at && dayjs(draft.built_at).isValid() ? dayjs(draft.built_at).format("YYYY") : undefined;
    const block = toStringOrUndefined(draft.block);
    const town = block ?? toStringOrUndefined(draft.town);

    return {
      // Step1: 基本情報
      name: toStringOrUndefined(draft.name),
      prefecture: toStringOrUndefined(draft.prefecture),
      city: toStringOrUndefined(draft.city),
      town,
      block,
      railway: toStringOrUndefined(draft.railway),
      station: toStringOrUndefined(draft.station),
      built_at: builtAtYear,
      structure: toStringOrUndefined(draft.structure),
      room_number: toStringOrUndefined(draft.unit_detail?.room_number),
      floor: toStringOrUndefined(draft.unit_detail?.floor),
      occupied_area_m2: toStringOrUndefined(draft.unit_detail?.occupied_area_m2),
      layout: toStringOrUndefined(draft.unit_detail?.layout),

      // Step2: 価格・ローン
      bought_at: boughtAt.yearMonth,
      bought_at_day: boughtAt.day,
      total_price_of_property: toStringOrUndefined(draft.total_price_of_property),
      priceof_tax: toStringOrUndefined(draft.priceof_tax as unknown as number | string | null),
      priceof_building: toStringOrUndefined(draft.priceof_building),
      priceof_facilities: toStringOrUndefined(draft.priceof_facilities),
      priceof_land: toStringOrUndefined(draft.priceof_land),
      loan_amount: toStringOrUndefined(draft.loan_amount),
      loan_interest_rate_percentage: toStringOrUndefined(cashflow?.loan_interest_rate_percentage),
      payment_starts_at: paymentStartsAt.yearMonth,
      payment_period: toStringOrUndefined(draft.payment_period),
      property_acquisition_tax: toStringOrUndefined(draft.property_acquisition_tax),
      property_acquisition_tax_payment_month: toStringOrUndefined(draft.property_acquisition_tax_payment_month),
      misc_expences_at_bought: toStringOrUndefined(draft.misc_expences_at_bought),

      // Step3: 収入
      monthly_fee: toStringOrUndefined(leaseContract?.monthly_fee ?? cashflow?.rent_income),
      monthly_management_fee: toStringOrUndefined(
        leaseContract?.monthly_management_fee ?? cashflow?.management_fee_income
      ),
      other_monthly_income: toStringOrUndefined(leaseContract?.other_monthly_income),
      type_of_the_income_of_the_beginning_of_contract: toStringOrUndefined(
        leaseContract?.type_of_the_income_of_the_beginning_of_contract
      ),
      security_deposit: toStringOrUndefined(leaseContract?.security_deposit),
      key_money: toStringOrUndefined(leaseContract?.key_money),
      outsourcing_fee_rate_of_new_lease_contract: toStringOrUndefined(
        leaseContract?.outsourcing_fee_rate_of_new_lease_contract
      ),
      outsourcing_fee_rate_of_updating_lease_contract: toStringOrUndefined(
        leaseContract?.outsourcing_fee_rate_of_updating_lease_contract
      ),
      starts_at: leaseContractStartsAt.yearMonth,
      starts_at_day: leaseContractStartsAt.day,
      ends_at: leaseContractEndsAt.yearMonth,
      ends_at_day: leaseContractEndsAt.day,
      building_management_cost: toStringOrUndefined(cashflow?.building_management_cost),
      repair_fund_cost: toStringOrUndefined(cashflow?.repair_fund_cost),
      management_cost: toStringOrUndefined(cashflow?.management_cost),
      misc_cost: toStringOrUndefined(cashflow?.misc_cost),
      communication_cost: toStringOrUndefined(cashflow?.communication_cost),
    } as const;
  };

  const applyDraftToForm = (draft: PropertyBuilder) => {
    const mapped = mapDraftToFormSchema(draft);
    const step1Keys = new Set([
      "name",
      "prefecture",
      "city",
      "town",
      "block",
      "railway",
      "station",
      "walk",
      "built_at",
      "structure",
      "room_number",
      "floor",
      "occupied_area_m2",
      "layout",
      "management_type",
      "sales_status",
      "property_image",
    ]);
    const doNotApplyAiDraftKeys = new Set([
      // 購入予定年月日（購入年月日）
      "bought_at",
      "bought_at_day",
      // 初回返済月
      "payment_starts_at",
      // 賃貸借契約の開始月/終了月
      "starts_at",
      "starts_at_day",
      "ends_at",
      "ends_at_day",
    ]);
    const mappedForStep2AndAfter = Object.fromEntries(
      Object.entries(mapped).filter(
        ([key, value]) => value !== undefined && !step1Keys.has(key) && !doNotApplyAiDraftKeys.has(key)
      )
    );

    setFormData((prev) => ({
      ...prev,
      ...mappedForStep2AndAfter,
    }));

    const setFormValue = setValue as unknown as (name: string, value: unknown) => void;
    Object.entries(mappedForStep2AndAfter).forEach(([key, value]) => {
      if (value === undefined) return;
      setFormValue(key, value);
    });
  };

  const handleAiCreate = async () => {
    if (isAiCreatingRef.current) return;
    isAiCreatingRef.current = true;
    const isValid = await validate();
    if (!isMountedRef.current) {
      isAiCreatingRef.current = false;
      return;
    }
    if (!isValid) {
      isAiCreatingRef.current = false;
      setSeverError("必須項目を入力してください。");
      return;
    }

    const values = getValues();
    const propertyName = String(values?.name || "").trim();
    if (!propertyName) {
      isAiCreatingRef.current = false;
      setSeverError("マンション・アパート名を入力してください。");
      return;
    }

    sendEventToAppMeasurementTool(AppEventTokenEnum.OwnPropertyAiInputStart);
    setIsAiCreating(true);

    try {
      const toNumberOrUndefined = (v: unknown) => {
        if (v === null || v === undefined) return undefined;
        const n = typeof v === "number" ? v : Number(String(v));
        return Number.isFinite(n) ? n : undefined;
      };
      const toIntOrUndefined = (v: unknown) => {
        const n = toNumberOrUndefined(v);
        return n === undefined ? undefined : Math.trunc(n);
      };

      // Step1 UIの表示ラベル → APIで期待するEnum値へ変換
      const toManagementTypeEnumOrUndefined = (v: unknown) => {
        if (!v) return undefined;
        const str = String(v).trim();
        if (!str) return undefined;
        if (Object.values(PropertyManagementTypeEnum).includes(str as PropertyManagementTypeEnum)) {
          return str as PropertyManagementTypeEnum;
        }
        const fromUiLabel = NEW_MANAGEMENT_TYPE_KEY_VALUES.find((it) => it.value === str)?.key;
        if (fromUiLabel) return fromUiLabel as PropertyManagementTypeEnum;
        if (str === "未選択") return PropertyManagementTypeEnum.NotSelected;
        return undefined;
      };

      const toSalesStatusEnumOrUndefined = (v: unknown) => {
        if (!v) return undefined;
        const str = String(v).trim();
        if (!str) return undefined;
        if (Object.values(PropertySalesStatusEnum).includes(str as PropertySalesStatusEnum)) {
          return str as PropertySalesStatusEnum;
        }
        return undefined;
      };

      const res = await fp.v1PropertiesAiDraftOwnPost(
        {
          property_name: propertyName,
          prefecture: toStringOrUndefined(values?.prefecture),
          city: toStringOrUndefined(values?.city),
          town: toStringOrUndefined(values?.town),
          railway: toStringOrUndefined(values?.railway),
          station: toStringOrUndefined(values?.station),
          walk: toIntOrUndefined(values?.walk),
          built_at: toStringOrUndefined(values?.built_at),
          occupied_area_m2: toNumberOrUndefined(values?.occupied_area_m2),
          layout: toStringOrUndefined(values?.layout),
          structure: toStringOrUndefined(values?.structure),
          room_number: toStringOrUndefined(values?.room_number),
          floor: toIntOrUndefined(values?.floor),
          management_type: toManagementTypeEnumOrUndefined(values?.management_type),
          sales_status: toSalesStatusEnumOrUndefined(values?.sales_status),
          bought_at: toYearMonthOrUndefined(values?.bought_at),
          bought_at_day: toIntOrUndefined(values?.bought_at_day),
        },
        { withCredentials: true }
      );
      if (!isMountedRef.current) return;
      applyDraftToForm(res.data);
      sendEventToAppMeasurementTool(AppEventTokenEnum.OwnPropertyAiInputSuccess);
      if (onAiReview) onAiReview();
      else onNext();
    } catch (error: unknown) {
      sendEventToAppMeasurementTool(AppEventTokenEnum.OwnPropertyAiInputFailed);
      if (!isMountedRef.current) return;
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "AIおまかせ入力が失敗しました。時間をおいて再度お試しください。";
      setSeverError(message);
    } finally {
      isAiCreatingRef.current = false;
      if (isMountedRef.current) setIsAiCreating(false);
    }
  };

  const handleClose = React.useCallback(() => {
    setSeverError("");
  }, []);

  // 築年の選択肢
  const currentYear = new Date().getFullYear();
  const years = Array.from(new Array(90), (val, index) => currentYear - 70 + index); // Array of years from current year - 70 to current year + 20
  const builtAtYearOptions = years.map((year) => ({ value: year.toString(), label: year.toString() }));

  return (
    <>
      <Box>
        <Box className={classes.content} data-registration-content>
          {isBuildingsSearching && (
            <Box>
              <Typography variant="subtitle2" color="inherit">
                マンション検索中...
                <CircularProgress style={{ marginLeft: "4px" }} size={14} />
              </Typography>
            </Box>
          )}
          {/* 自動入力 */}
          <RegistrationSection title="建物と部屋" />
          <Controller
            name="name"
            control={control}
            defaultValue={formData?.name || ""}
            render={({ ...field }) => (
              <RegistrationAutocomplete
                registrationLabel="マンション・アパート名"
                registrationOnBlur={field.onBlur}
                registrationError={errors.name?.message}
                registrationRequired={true}
                size="small"
                freeSolo
                disableClearable
                options={buildingOptions}
                // ref={field.ref}
                value={field.value}
                // MEMO: getOptionLabelのoptionにはoptionsでvalueを含めなくても直接入力されたstring型のvalueも含まれるためstring型の考慮をしている
                getOptionLabel={(option: HowMaBuilding | string) => {
                  return typeof option === "string" ? option : `${option.name}`;
                }}
                // MEMO: renderOptionのoptionには直接入力されたstring型のvalueは入ってこないためstring型の考慮不要
                renderOption={(option: HowMaBuilding) => (
                  <Box>
                    <Typography variant="body2" component="div">
                      {option.name}
                    </Typography>
                    <Typography variant="caption">({option.area.name})</Typography>
                  </Box>
                )}
                renderInput={(params) => {
                  return (
                    <CustomAutoCompleteForm
                      params={params}
                      label="マンション・アパート名"
                      helperText="マンション・アパート名から物件情報を自動入力する"
                      required
                      ref={field.ref}
                      error={!!errors.name}
                      errorText={errors.name?.message}
                    />
                  );
                }}
                // MEMO: オートコンプリートの選択肢の中から選択した場合の処理
                onChange={(e, selected: HowMaBuilding) => {
                  // 手動入力のEnter確定時にもonChangeが発火してしまい、その場合には文字列が入ってきてしまうため、
                  // 選択肢の中からの選択ではない文字列(=string)の場合は後続処理スキップ
                  if (typeof selected === "string") return;

                  // マンション情報自動入力
                  setSelectedHowMaBuilding(selected);

                  // 部屋詳細APIを実行して部屋情報取得
                  fetchRoomOptions(selected.id);
                }}
                // MEMO: 手動入力の場合の処理
                // onInputChangeのdataに渡される値は、オートコンプリート選択肢から選択された場合も、選択されたoptionにあたるgetOptionLabelの値が利用されるため、Building型は入らずstring型のみ
                onInputChange={(e: React.ChangeEvent, inputed: string, reason: AutocompleteInputChangeReason) => {
                  // 候補選択時でもinputが発火してしまうため、手動入力以外は検索処理を実行させない
                  if (reason !== "input") return;
                  // 検索実行
                  buildingsSearch(inputed);
                }}
                // MEMO: inputエリアフォーカス時に実行したい処理
                onFocus={() => {}}
              />
            )}
          />

          {/* 部屋選択（候補がある場合のみ） */}
          {roomOptions.length > 0 && (
            <RegistrationAutocomplete
              registrationLabel="部屋"
              registrationError={errors.room_number?.message}
              registrationRequired={true}
              id="room"
              options={roomOptions}
              value={selectedHowMaRoom}
              size="small"
              getOptionLabel={(option: HowMaRoom) => (option ? option.name : "")}
              renderOption={(option: HowMaRoom) => (
                <Box>
                  <Typography variant="body2" component="div">
                    {option.name}
                  </Typography>
                  <Typography variant="caption">
                    {option.floor_number}階 / {option.m2}m²
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <CustomAutoCompleteForm
                  params={params}
                  label="部屋"
                  helperText="部屋を選ぶと、号室/所在階/専有面積/間取りを自動入力します"
                  ref={params.InputProps.ref}
                />
              )}
              onChange={(e, selected: HowMaRoom | null) => {
                setSelectedHowMaRoom(selected);
              }}
            />
          )}

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                control={control}
                name="room_number"
                defaultValue={formData?.room_number || ""}
                render={({ ...field }) => (
                  <CustomForm
                    {...field}
                    label="号室"
                    required
                    error={!!errors.room_number}
                    errorText={errors.room_number?.message}
                    onChange={field.onChange}
                    placeHolder="102"
                    unitSuffix="号室"
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="floor"
                control={control}
                defaultValue={formData.floor || ""}
                render={({ ...field }) => (
                  <>
                    <CustomForm
                      {...field}
                      label="所在階"
                      required
                      error={!!errors.floor}
                      errorText={errors.floor?.message}
                      onChange={field.onChange}
                      placeHolder="1"
                      unitSuffix="階"
                    />
                    <Typography variant="caption">※一棟の場合は「0」と入力</Typography>
                  </>
                )}
              />
            </Grid>
          </Grid>

          <RegistrationSection title="住所" />

          <Controller
            control={control}
            name="prefecture"
            defaultValue={formData.prefecture || ""}
            render={({ ...field }) => (
              <RegistrationAutocomplete
                registrationLabel="都道府県"
                registrationOnBlur={field.onBlur}
                registrationError={errors.prefecture?.message}
                registrationRequired={true}
                id="prefecture"
                options={!field.value || field.value === "" ? [field.value, ...prefectures] : prefectures}
                value={field.value}
                size="small"
                filterOptions={(options, state) => {
                  const defaultFiltered = defaultFilterOptions(options, state);
                  return defaultFiltered.filter((option) => option !== "" && option !== null);
                }}
                getOptionLabel={(option) => {
                  return !option ? "" : option;
                }}
                renderOption={(params) => <>{params}</>}
                renderInput={(params) => {
                  return (
                    <CustomAutoCompleteForm
                      params={params}
                      label="都道府県"
                      required
                      ref={field.ref}
                      error={!!errors.prefecture}
                      errorText={errors.prefecture?.message}
                    />
                  );
                }}
                onChange={(e, data) => {
                  field.onChange(data);
                  setValue("city", "");
                  setValue("town", "");
                }}
              />
            )}
          />

          <Controller
            name="city"
            control={control}
            defaultValue={formData.city || ""}
            render={({ ...field }) => (
              <RegistrationAutocomplete
                registrationLabel="市区町村"
                registrationOnBlur={field.onBlur}
                registrationError={errors.city?.message}
                registrationRequired={true}
                id="city"
                options={!field.value || field.value === "" ? [field.value, ...cities] : cities}
                value={field.value}
                size="small"
                filterOptions={(options, state) => {
                  const defaultFiltered = defaultFilterOptions(options, state);
                  return defaultFiltered.filter((option) => option !== "" && option !== null);
                }}
                getOptionLabel={(option) => {
                  return !option ? "" : option;
                }}
                getOptionSelected={(option, value) => option === value}
                renderOption={(params) => <>{params}</>}
                renderInput={(params) => {
                  return (
                    <CustomAutoCompleteForm
                      params={params}
                      label="市区町村"
                      required
                      ref={field.ref}
                      error={!!errors.city}
                      errorText={errors.city?.message}
                    />
                  );
                }}
                onChange={(e, data) => {
                  field.onChange(data);
                  setValue("town", "");
                }}
                disabled={!watchValues.prefecture || watchValues.prefecture === ""}
              />
            )}
          />
          <Controller
            control={control}
            name="town"
            error={!!errors.town}
            helperText={errors.town?.message}
            defaultValue={formData.town || ""}
            render={({ ...field }) => (
              <RegistrationAutocomplete
                registrationLabel="番地"
                registrationOnBlur={field.onBlur}
                registrationError={errors.town?.message}
                registrationRequired={true}
                id="town"
                options={!field.value || field.value === "" ? [field.value, ...towns] : towns}
                value={field.value}
                size="small"
                filterOptions={(options, state) => {
                  const defaultFiltered = defaultFilterOptions(options, state);
                  return defaultFiltered.filter((option) => option !== "" && option !== null);
                }}
                getOptionLabel={(option) => {
                  return !option ? "" : option;
                }}
                renderOption={(params) => <>{params}</>}
                renderInput={(params) => {
                  return (
                    // <TextField {...params} variant="outlined" margin="dense" label="入力して検索" fullWidth/>
                    <CustomAutoCompleteForm
                      params={params}
                      label="番地"
                      required
                      ref={field.ref}
                      error={!!errors.town}
                      errorText={errors.town?.message}
                    />
                  );
                }}
                onChange={(e, data) => {
                  field.onChange(data);
                }}
                disabled={!watchValues.city || watchValues.city === ""}
                // className={classes.disabled}
              />
            )}
          />

          <Controller
            control={control}
            name="railway"
            defaultValue={formData.railway || ""}
            render={({ ...field }) => (
              <RegistrationAutocomplete
                registrationLabel="路線名"
                registrationOnBlur={field.onBlur}
                registrationError={errors.railway?.message}
                registrationRequired={true}
                id="railway"
                options={
                  !field.value || field.value === ""
                    ? [field.value, ...railways.map((v) => v.name)]
                    : railways.map((v) => v.name)
                }
                value={field.value}
                size="small"
                filterOptions={(options, state) => {
                  const defaultFiltered = defaultFilterOptions(options, state);
                  return defaultFiltered.filter((option) => option !== "" && option !== null);
                }}
                getOptionLabel={(option) => {
                  return !option ? "" : option;
                }}
                renderOption={(params) => <>{params}</>}
                renderInput={(params) => {
                  return (
                    <CustomAutoCompleteForm
                      params={params}
                      label="路線名"
                      required
                      ref={field.ref}
                      error={!!errors.railway}
                      errorText={errors.railway?.message}
                    />
                  );
                }}
                onChange={(e, data) => {
                  setValue("station", "");
                  field.onChange(data);
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="station"
            defaultValue={formData.station || ""}
            render={({ ...field }) => (
              <RegistrationAutocomplete
                registrationLabel="駅名"
                registrationOnBlur={field.onBlur}
                registrationError={errors.station?.message}
                registrationRequired={true}
                id="station"
                options={!field.value || field.value === "" ? [field.value, ...stations] : stations}
                value={field.value}
                size="small"
                filterOptions={(options, state) => {
                  const defaultFiltered = defaultFilterOptions(options, state);
                  return defaultFiltered.filter((option) => option !== "" && option !== null);
                }}
                getOptionLabel={(option) => {
                  return !option ? "" : option;
                }}
                renderOption={(params) => <>{params}</>}
                renderInput={(params) => {
                  return (
                    <CustomAutoCompleteForm
                      params={params}
                      label="駅名"
                      required
                      ref={field.ref}
                      error={!!errors.station}
                      errorText={errors.station?.message}
                    />
                  );
                }}
                onChange={(e, data) => field.onChange(data)}
                disabled={watchValues.railway === "" || !watchValues.railway}
                // className={classes.disabled}
              />
            )}
          />

          <Controller
            name="walk"
            control={control}
            defaultValue={formData.walk || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="駅徒歩"
                required
                error={!!errors.walk}
                errorText={errors.walk?.message}
                onChange={field.onChange}
                placeHolder="1"
                unitSuffix="分"
                isShort
              />
            )}
          />
          <RegistrationSection title="建物の情報" />
          <Controller
            name="built_at"
            control={control}
            defaultValue={
              formData.built_at ? dayjs(formData.built_at).format("YYYY") : dayjs(new Date().getDate()).format("YYYY")
            }
            render={({ ...field }) => (
              <CustomSelector
                {...field}
                label="築年"
                required
                error={!!errors.built_at}
                errorText={errors.built_at?.message}
                onChange={field.onChange}
                options={builtAtYearOptions}
                placeHolder={`${currentYear}`}
                suffix="年"
              />
            )}
          />
          <Controller
            name="occupied_area_m2"
            control={control}
            defaultValue={formData.occupied_area_m2 || ""}
            render={({ ...field }) => (
              <>
                <CustomForm
                  {...field}
                  label="専有面積"
                  required
                  error={!!errors.occupied_area_m2}
                  errorText={errors.occupied_area_m2?.message}
                  onChange={field.onChange}
                  placeHolder="32"
                  unitSuffix="m²"
                  isShort
                />
                <Typography variant="caption">※一棟の場合は「0」と入力</Typography>
              </>
            )}
          />
          <Controller
            name="layout"
            control={control}
            defaultValue={formData.layout || ""}
            render={({ ...field }) => (
              <CustomSelector
                {...field}
                label="間取り"
                onChange={field.onChange}
                error={!!errors.layout}
                errorText={errors.layout?.message}
                options={Layout.map((layout) => ({ label: layout, value: layout }))}
                placeHolder="未選択"
              />
            )}
          />
          <Controller
            name="structure"
            control={control}
            defaultValue={formData.structure || PropertyStructureEnum.Rc造鉄筋コンクリート造}
            render={({ ...field }) => (
              <CustomSelector
                {...field}
                label="構造"
                onChange={field.onChange}
                error={!!errors.structure}
                errorText={errors.structure?.message}
                options={[
                  { label: "SRC造(鉄骨鉄筋コンクリート造)", value: "SRC造(鉄骨鉄筋コンクリート造)" },
                  { label: "RC造(鉄筋コンクリート造)", value: "RC造(鉄筋コンクリート造)" },
                  { label: "S造(重量鉄骨造)", value: "S造(重量鉄骨造)" },
                  { label: "木造", value: "木造" },
                ]}
              />
            )}
          />
          <RegistrationSection title="管理と売却" />
          <Controller
            name="management_type"
            control={control}
            defaultValue={formData.management_type || NEW_MANAGEMENT_TYPE_KEY_VALUES[0].value}
            render={({ ...field }) => (
              <CustomSelector
                {...field}
                label="管理形態"
                required
                onChange={field.onChange}
                error={!!errors.management_type}
                errorText={errors.management_type?.message}
                options={NEW_MANAGEMENT_TYPE_KEY_VALUES.map((managementType) => {
                  return { value: managementType.value, label: managementType.value };
                })}
              />
            )}
          />
          <Controller
            name="sales_status"
            control={control}
            rules={{
              required: "売却状況を選択してください。",
            }}
            defaultValue={formData.sales_status === PropertySalesStatusEnum.未選択 ? "" : formData.sales_status || ""}
            render={({ ...field }) => (
              <CustomSelector
                {...field}
                label="売却状況"
                required
                onChange={field.onChange}
                error={!!errors.sales_status}
                errorText={errors.sales_status?.message}
                options={Object.values(PropertySalesStatusEnum)
                  .filter((salesStatus) => salesStatus !== PropertySalesStatusEnum.未選択)
                  .map((salesStatus) => {
                    return { value: salesStatus, label: salesStatus };
                  })}
              />
            )}
          />
          <RegistrationSection title="購入" />
          <Box mb={1}>
            <CustomFormLabel label="購入年月日" required info="購入した年月日を選択してください。" />
          </Box>
          <Controller
            name="bought_at"
            control={control}
            defaultValue={formData.bought_at || null}
            render={({ ...field }) => (
              <DatePickerComponent
                required
                labelLight="年月"
                error={!!errors.bought_at}
                errorText={errors.bought_at?.message as string}
                {...field}
              />
            )}
          />

          <Controller
            name="bought_at_day"
            control={control}
            defaultValue={formData.bought_at_day || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="日にち"
                helperText="不明の場合は値を「1日」のままお進みください。"
                error={!!errors.bought_at_day}
                errorText={errors.bought_at_day?.message}
                placeHolder="1"
                unitSuffix="日"
              />
            )}
          />

          <RegistrationSection title="写真" />

          <Controller
            name="property_image"
            control={control}
            defaultValue={formData.property_image || ""}
            render={({ ...field }) => (
              <UploadComponent
                {...field}
                name={field.name}
                error={!!errors.property_image}
                errorText={errors.property_image?.message}
                setError={setError}
                onChange={field.onChange}
                clearErrors={clearErrors}
              />
            )}
          />
          {/* AIおまかせ入力 */}
          {registrationMode !== "manual" && (
            <RegistrationActions>
              <Typography className={classes.aiDescription}>
                AIが推定した内容を確認・修正してから登録できます。
              </Typography>
              <Box mt={2}>
                <CustomButton customVariant="filled" onClick={handleAiCreate} disabled={isAiCreating}>
                  {isAiCreating ? "入力中..." : "AIで入力する"}
                </CustomButton>
              </Box>
            </RegistrationActions>
          )}
          {registrationMode !== "ai" && (
            <RegistrationActions>
              <Box mt={2}>
                <CustomButton type="button" customVariant="filled" onClick={handleNext}>
                  次へ
                </CustomButton>
              </Box>
            </RegistrationActions>
          )}
        </Box>
      </Box>

      {/* AI入力中オーバーレイ */}
      {isAiCreating && (
        <Box className={classes.loadingOverlay}>
          <Box
            role="status"
            aria-live="polite"
            className={classes.loadingCard}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <img src="/assets/image/ds_v2_registration_spinner.svg" alt="" className={classes.spinner} />
            <Box mt={2}>
              <Typography className={classes.aiLoadingTitle}>AIが物件を調べています</Typography>
              <Typography className={classes.aiLoadingDescription}>
                相場・利回り・経費の目安を集めています。最大60秒ほどかかります。
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Toast open={!!serverError} variant="error" message={serverError} onClose={handleClose} />
    </>
  );
};

export default AddNewOwnPropertyBasicPage;
